import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft, ArrowRight, Save, Send, Plus, Trash2, Check,
  ListChecks, Edit3, Layers, FileCheck, Search, Image as ImageIcon,
  Upload, X, ListTodo, HelpCircle, Link2, FileText,
} from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { Stepper } from '@/components/ui/Stepper';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { confirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { SymbolPicker } from '@/components/SymbolPicker';
import { TagInput } from '@/components/TagInput';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import type {
  SubjectGroup, GradeLevel, DifficultyLevel, CognitiveLevel, QuestionType,
} from '@/types';

interface OptionDraft { text: string; is_correct: boolean; explanation: string; }
interface PairDraft { left: string; right: string; }

interface WizardData {
  subject_group_id: string;
  grade_level_id: string;
  /** v7 supports multiple linked indicators per question */
  learning_indicator_ids: number[];
  cognitive_level_id: string;
  difficulty_level_id: string;
  topic: string;
  question_type: QuestionType | '';
  question_text: string;
  points: number;
  options: OptionDraft[];
  pairs: PairDraft[];
  image_path: string;
  /** Single explanation shared by the whole question (v7 pattern for TF / matching) */
  explanation: string;
  /** Essay-only fields */
  model_answer: string;
  scoring_criteria: string;
  max_score: number;
  tag_ids: number[];
}

const EMPTY: WizardData = {
  subject_group_id: '',
  grade_level_id: '',
  learning_indicator_ids: [],
  cognitive_level_id: '',
  difficulty_level_id: '',
  topic: '',
  question_type: '',
  question_text: '',
  points: 1,
  options: [
    { text: '', is_correct: true, explanation: '' },
    { text: '', is_correct: false, explanation: '' },
    { text: '', is_correct: false, explanation: '' },
    { text: '', is_correct: false, explanation: '' },
  ],
  pairs: [
    { left: '', right: '' },
    { left: '', right: '' },
    { left: '', right: '' },
  ],
  image_path: '',
  explanation: '',
  model_answer: '',
  scoring_criteria: '',
  max_score: 10,
  tag_ids: [],
};

const TYPES_BASE: {
  value: QuestionType;
  labelKey: string;
  descKey: string;
  icon: React.ReactNode;
  color: string;
}[] = [
  {
    value: 'single_choice',
    labelKey: 'wizard.type.single_choice',
    descKey: 'wizard.type.single_choice_desc',
    icon: <ListTodo className="w-6 h-6" />,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
  },
  {
    value: 'multiple_answer',
    labelKey: 'wizard.type.multiple_answer',
    descKey: 'wizard.type.multiple_answer_desc',
    icon: <ListChecks className="w-6 h-6" />,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
  },
  {
    value: 'true_false',
    labelKey: 'wizard.type.true_false',
    descKey: 'wizard.type.true_false_desc',
    icon: <HelpCircle className="w-6 h-6" />,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
  },
  {
    value: 'matching',
    labelKey: 'wizard.type.matching',
    descKey: 'wizard.type.matching_desc',
    icon: <Link2 className="w-6 h-6" />,
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  },
  {
    value: 'essay',
    labelKey: 'wizard.type.essay',
    descKey: 'wizard.type.essay_desc',
    icon: <FileText className="w-6 h-6" />,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
  },
];

// STEPS labels resolved via t() inside the component

interface MetaPayload {
  subject_groups?: SubjectGroup[];
  grade_levels?: GradeLevel[];
  difficulty_levels?: DifficultyLevel[];
  cognitive_levels?: CognitiveLevel[];
}

const DRAFT_KEY = 'baiboon_v8_wizard_draft';
const CLONE_KEY = 'baiboon_v8_wizard_clone';

export function QuestionWizard() {
  const t = useT();
  const navigate = useNavigate();
  const params = useParams<{ id?: string }>();
  const editId = params.id ? parseInt(params.id, 10) : null;

  const STEPS = [
    { label: t('wizard.step.type'),    description: t('wizard.step.type_desc') },
    { label: t('wizard.step.basic'),   description: t('wizard.step.basic_desc') },
    { label: t('wizard.step.content'), description: t('wizard.step.content_desc') },
    { label: t('wizard.step.answers'), description: t('wizard.step.answers_desc') },
    { label: t('wizard.step.review'),  description: t('wizard.step.review_desc') },
  ];

  const [step, setStep] = useState(0);
  const [isClone, setIsClone] = useState(false);
  const [data, setData] = useState<WizardData>(() => {
    if (params.id) return EMPTY;
    try {
      const cloneRaw = localStorage.getItem(CLONE_KEY);
      if (cloneRaw) {
        const q = JSON.parse(cloneRaw);
        const opts = (q.options ?? q.answer_options ?? []) as any[];
        const indicators = q.learning_indicators as Array<{ id: number }> | undefined;
        return {
          subject_group_id: q.subject_group_id ? String(q.subject_group_id) : '',
          grade_level_id: q.grade_level_id ? String(q.grade_level_id) : '',
          learning_indicator_ids: indicators?.map((i: { id: number }) => i.id) ?? (q.learning_indicator_id ? [q.learning_indicator_id] : []),
          cognitive_level_id: q.cognitive_level_id ? String(q.cognitive_level_id) : '',
          difficulty_level_id: q.difficulty_level_id ? String(q.difficulty_level_id) : '',
          topic: q.topic ?? '',
          question_type: q.question_type ?? '',
          question_text: q.question_text ?? '',
          points: q.points ?? 1,
          options: opts.length > 0
            ? opts.map((o: any) => ({
                text: o.text ?? o.option_text ?? '',
                is_correct: o.is_correct == 1 || o.is_correct === true,
                explanation: o.explanation ?? '',
              }))
            : EMPTY.options,
          pairs: EMPTY.pairs,
          image_path: q.image_path ?? '',
          explanation: q.explanation ?? '',
          model_answer: q.model_answer ?? '',
          scoring_criteria: q.scoring_criteria ?? '',
          max_score: q.max_score ?? 10,
          tag_ids: (q.tags as Array<{ id: number }> | undefined)?.map((tg) => tg.id) ?? [],
        };
      }
    } catch {}
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) return { ...EMPTY, ...JSON.parse(raw) };
    } catch {}
    return EMPTY;
  });
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);

  const meta = useQuery({
    queryKey: ['metadata-full'],
    queryFn: () => api<{ data?: MetaPayload } & MetaPayload>('metadata/all.php').then((r) => r.data ?? r),
    staleTime: 5 * 60_000,
  });

  // Existing question (edit mode)
  const existing = useQuery({
    queryKey: ['question-edit', editId],
    queryFn: async () => {
      const r = await api<{ data?: { question?: any } & any }>(`questions/manage.php?id=${editId}`);
      return r.data?.question ?? r.data;
    },
    enabled: !!editId,
  });

  useEffect(() => {
    if (!existing.data || !editId) return;
    const q = existing.data;
    const opts = (q.options ?? q.answer_options ?? []) as any[];
    const indicators = q.learning_indicators as Array<{ id: number }> | undefined;
    setData({
      subject_group_id: q.subject_group_id ? String(q.subject_group_id) : '',
      grade_level_id: q.grade_level_id ? String(q.grade_level_id) : '',
      learning_indicator_ids: indicators?.map((i) => i.id) ?? (q.learning_indicator_id ? [q.learning_indicator_id] : []),
      cognitive_level_id: q.cognitive_level_id ? String(q.cognitive_level_id) : '',
      difficulty_level_id: q.difficulty_level_id ? String(q.difficulty_level_id) : '',
      topic: q.topic ?? '',
      question_type: q.question_type ?? '',
      question_text: q.question_text ?? '',
      points: q.points ?? 1,
      options: opts.length > 0
        ? opts.map((o) => ({
            text: o.text ?? o.option_text ?? '',
            is_correct: o.is_correct == 1 || o.is_correct === true,
            explanation: o.explanation ?? '',
          }))
        : EMPTY.options,
      pairs: EMPTY.pairs,
      image_path: q.image_path ?? '',
      explanation: q.explanation ?? '',
      model_answer: q.model_answer ?? '',
      scoring_criteria: q.scoring_criteria ?? '',
      max_score: q.max_score ?? 10,
      tag_ids: (q.tags as Array<{ id: number }> | undefined)?.map((tg) => tg.id) ?? [],
    });
  }, [existing.data, editId]);

  // On mount: clear clone key and show toast
  useEffect(() => {
    if (editId) return;
    try {
      const had = localStorage.getItem(CLONE_KEY);
      if (had) {
        localStorage.removeItem(CLONE_KEY);
        setIsClone(true);
        toast.info(t('wizard.clone_toast'));
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save (create mode only, not when cloned — user hasn't edited yet)
  useEffect(() => {
    if (editId) return;
    if (isClone) return;
    if (data === EMPTY) return;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(data));
        setSavedAt(new Date());
      } catch {}
    }, 600);
    return () => clearTimeout(timer);
  }, [data, editId, isClone]);

  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  }

  function update<K extends keyof WizardData>(k: K, v: WizardData[K]) {
    setData((d) => ({ ...d, [k]: v }));
  }

  function canProceed(): string | null {
    if (step === 0) {
      if (!data.question_type) return t('wizard.val.type');
    } else if (step === 1) {
      if (!data.subject_group_id) return t('wizard.val.subject');
      if (!data.grade_level_id) return t('wizard.val.grade');
      if (!data.topic.trim()) return t('wizard.val.topic');
    } else if (step === 2) {
      if (!data.question_text.trim()) return t('wizard.val.question');
      if (data.question_text.trim().length < 10) return t('wizard.val.question_short');
    } else if (step === 3) {
      if (data.question_type === 'matching') {
        if (data.pairs.filter((p) => p.left.trim() && p.right.trim()).length < 2)
          return t('wizard.val.pairs');
      } else if (data.question_type === 'true_false') {
        // baked-in below
      } else if (data.question_type === 'essay') {
        if (!data.model_answer.trim()) return t('wizard.val.essay');
      } else if (data.question_type === 'single_choice' || data.question_type === 'multiple_answer') {
        const valid = data.options.filter((o) => o.text.trim());
        if (valid.length < 2) return t('wizard.val.choices_min');
        if (!valid.some((o) => o.is_correct)) return t('wizard.val.choices_correct');
      }
    } else if (step === 4) {
      if (!reviewConfirmed) return t('wizard.val.confirm');
    }
    return null;
  }

  function proceed() {
    const err = canProceed();
    if (err) { toast.warning(err); return; }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }

  function buildPayload(submitForApproval: boolean) {
    let options: { text: string; is_correct: 0 | 1; explanation?: string }[] = [];
    if (data.question_type === 'single_choice' || data.question_type === 'multiple_answer') {
      options = data.options
        .filter((o) => o.text.trim())
        .map((o) => ({ text: o.text, is_correct: o.is_correct ? 1 : 0, explanation: o.explanation }));
    } else if (data.question_type === 'true_false') {
      options = [
        { text: 'ถูก', is_correct: data.options[0]?.is_correct ? 1 : 0, explanation: data.explanation },
        { text: 'ผิด', is_correct: data.options[0]?.is_correct ? 0 : 1, explanation: data.explanation },
      ];
    }
    return {
      ...data,
      subject_group_id: parseInt(data.subject_group_id, 10) || null,
      grade_level_id: parseInt(data.grade_level_id, 10) || null,
      learning_indicator_id: data.learning_indicator_ids[0] ?? null,
      learning_indicator_ids: data.learning_indicator_ids,
      cognitive_level_id: parseInt(data.cognitive_level_id, 10) || null,
      difficulty_level_id: parseInt(data.difficulty_level_id, 10) || null,
      options,
      pairs: data.question_type === 'matching' ? data.pairs.filter((p) => p.left && p.right) : [],
      submit_for_approval: submitForApproval ? 1 : 0,
    };
  }

  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      const payload = buildPayload(submit);
      let qId: number | null = editId;
      if (editId) {
        await api(`questions/manage.php?id=${editId}`, { method: 'PUT', json: payload });
      } else {
        const r = await api<{ data?: { questionId?: number } }>('questions/index.php', { method: 'POST', json: payload });
        qId = r.data?.questionId ?? null;
      }
      if (qId && data.tag_ids.length > 0) {
        await api('questions/tags.php', { method: 'POST', json: { question_id: qId, tag_ids: data.tag_ids } }).catch(() => {});
      }
    },
    onSuccess: (_, submit) => {
      toast.success(
        editId
          ? (submit ? t('wizard.success_update_submit') : t('wizard.success_update'))
          : (submit ? t('wizard.success_submit') : t('wizard.success_draft'))
      );
      clearDraft();
      navigate('/questions');
    },
    onError: (e: Error) => toast.error(t('error.save_failed'), e.message),
  });

  return (
    <div className="space-y-5 max-w-5xl mx-auto">
      <PageHeader
        title={editId ? t('wizard.title_edit') : t('wizard.title_new')}
        description={editId ? `${t('wizard.title_edit')} #${editId}` : t('wizard.desc_new')}
        breadcrumbs={[{ label: t('wizard.breadcrumb_my_q'), to: '/questions' }, { label: editId ? `${t('wizard.breadcrumb_new')} #${editId}` : t('wizard.breadcrumb_new') }]}
        actions={
          <Button
            variant="ghost"
            leftIcon={<ArrowLeft className="w-4 h-4" />}
            onClick={async () => {
              const ok = await confirmDialog({
                title: t('wizard.exit_confirm'),
                description: t('wizard.exit_desc'),
                destructive: true,
                confirmLabel: t('wizard.exit'),
              });
              if (ok) navigate('/questions');
            }}
          >
            {t('wizard.exit')}
          </Button>
        }
      />

      <Card className="p-5">
        <Stepper steps={STEPS} current={step} onJump={(i) => i <= step && setStep(i)} />
        {!editId && savedAt && (
          <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1">
            <Save className="w-3 h-3" />
            <span>{t('wizard.autosave')} {savedAt.toLocaleTimeString('th-TH')}</span>
          </p>
        )}
      </Card>

      <Card>
        <CardBody className="space-y-5">
          {step === 0 && <StepTypeSelect data={data} update={update} />}
          {step === 1 && <StepBasic data={data} update={update} meta={meta.data ?? {}} />}
          {step === 2 && <StepContent data={data} update={update} />}
          {step === 3 && <StepAnswers data={data} update={update} />}
          {step === 4 && (
            <StepReview
              data={data}
              meta={meta.data ?? {}}
              confirmed={reviewConfirmed}
              setConfirmed={setReviewConfirmed}
            />
          )}
        </CardBody>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          leftIcon={<ArrowLeft className="w-4 h-4" />}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          {t('btn.previous')}
        </Button>

        <div className="flex flex-wrap items-center gap-2">
          {step === STEPS.length - 1 ? (
            <>
              <Button
                variant="secondary"
                leftIcon={<Save className="w-4 h-4" />}
                loading={save.isPending && !save.variables}
                disabled={!reviewConfirmed}
                onClick={() => save.mutate(false)}
              >
                {t('wizard.save_draft')}
              </Button>
              <Button
                leftIcon={<Send className="w-4 h-4" />}
                loading={save.isPending && !!save.variables}
                disabled={!reviewConfirmed}
                onClick={() => save.mutate(true)}
              >
                {t('wizard.send_approval')}
              </Button>
            </>
          ) : (
            <Button rightIcon={<ArrowRight className="w-4 h-4" />} onClick={proceed}>
              {t('btn.next')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step components ─────────────────────────────────

interface StepProps {
  data: WizardData;
  update: <K extends keyof WizardData>(k: K, v: WizardData[K]) => void;
}

function StepTypeSelect({ data, update }: StepProps) {
  const t = useT();
  return (
    <>
      <SectionHeader icon={<ListChecks className="w-4 h-4" />} title={t('wizard.type_section')} subtitle={t('wizard.type_subtitle')} />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TYPES_BASE.map((type) => {
          const active = data.question_type === type.value;
          return (
            <button
              key={type.value}
              type="button"
              onClick={() => update('question_type', type.value)}
              className={cn(
                'text-left p-5 border-2 rounded-xl transition-all',
                active
                  ? 'border-brand-500 bg-brand-50/40 ring-2 ring-brand-200 shadow-sm'
                  : 'border-slate-200 hover:border-slate-300 bg-white hover:shadow-sm'
              )}
            >
              <div className={cn('inline-flex w-12 h-12 rounded-xl items-center justify-center mb-3', type.color)}>
                {type.icon}
              </div>
              <div className="text-sm font-semibold text-slate-900 leading-tight">
                {t(type.labelKey)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                {t(type.descKey)}
              </div>
              {active && (
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-700">
                  <Check className="w-3.5 h-3.5" /> {t('wizard.selected_label')}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}

function StepBasic({ data, update, meta }: StepProps & { meta: MetaPayload }) {
  const t = useT();
  const [search, setSearch] = useState('');

  // Indicators are async — load when subject + grade chosen
  const indicators = useQuery({
    queryKey: ['indicators', data.subject_group_id, data.grade_level_id],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (data.subject_group_id) p.set('subject_group_id', data.subject_group_id);
      if (data.grade_level_id) p.set('grade_level_id', data.grade_level_id);
      const r = await api<{ data?: { learningIndicators?: Array<{ id: number; code: string; title: string }> } }>(`learning-indicators.php?${p}`);
      return r.data?.learningIndicators ?? [];
    },
    enabled: !!data.subject_group_id && !!data.grade_level_id,
  });

  const indicatorsList = indicators.data ?? [];
  const filtered = search
    ? indicatorsList.filter((i) => i.code.toLowerCase().includes(search.toLowerCase()) || i.title.toLowerCase().includes(search.toLowerCase()))
    : indicatorsList;

  function toggleIndicator(id: number) {
    const set = new Set(data.learning_indicator_ids);
    set.has(id) ? set.delete(id) : set.add(id);
    update('learning_indicator_ids', Array.from(set));
  }

  return (
    <>
      <SectionHeader icon={<Layers className="w-4 h-4" />} title={t('wizard.basic_section')} subtitle={t('wizard.basic_subtitle')} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label={t('wizard.subject_label')}
          value={data.subject_group_id}
          onChange={(e) => update('subject_group_id', e.target.value)}
        >
          <option value="">-- {t('wizard.subject_label').replace(' *', '')} --</option>
          {(meta.subject_groups ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </Select>
        <Select
          label={t('wizard.grade_label')}
          value={data.grade_level_id}
          onChange={(e) => update('grade_level_id', e.target.value)}
        >
          <option value="">-- {t('wizard.grade_label').replace(' *', '')} --</option>
          {(meta.grade_levels ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </Select>
      </div>

      <Input
        label={t('wizard.topic_label')}
        placeholder={t('wizard.topic_ph')}
        value={data.topic}
        onChange={(e) => update('topic', e.target.value)}
      />

      {/* Multi-select indicators (matches v7) */}
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
          {t('wizard.indicator_label')} {data.learning_indicator_ids.length > 0 && (
            <Badge tone="brand" className="ml-1">{data.learning_indicator_ids.length}</Badge>
          )}
        </label>
        {!data.subject_group_id || !data.grade_level_id ? (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500">
            {t('wizard.indicator_hint')}
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200">
            <div className="p-2.5 border-b border-slate-200 bg-slate-50">
              <Input
                leftIcon={<Search className="w-4 h-4" />}
                placeholder={t('wizard.indicator_search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="max-h-72 overflow-y-auto p-2 space-y-1">
              {indicators.isLoading ? (
                <div className="p-4 text-center text-sm text-slate-400">{t('wizard.indicator_loading')}</div>
              ) : filtered.length === 0 ? (
                <div className="p-4 text-center text-sm text-slate-400">
                  {search ? t('wizard.indicator_no_match') : t('wizard.indicator_empty')}
                </div>
              ) : (
                filtered.map((i) => (
                  <label
                    key={i.id}
                    className="flex items-start gap-2.5 p-2 rounded-md hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={data.learning_indicator_ids.includes(i.id)}
                      onChange={() => toggleIndicator(i.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <Badge tone="info" className="font-mono text-[10px]">{i.code}</Badge>
                        <span className="text-sm text-slate-900 truncate">{i.title}</span>
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Select
          label={t('wizard.bloom_label')}
          value={data.cognitive_level_id}
          onChange={(e) => update('cognitive_level_id', e.target.value)}
        >
          <option value="">-- {t('wizard.bloom_label')} --</option>
          {(meta.cognitive_levels ?? [])
            .slice()
            .sort((a, b) => (a.bloom_level ?? 0) - (b.bloom_level ?? 0))
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.bloom_level} · {c.name}{c.name_en ? ` (${c.name_en})` : ''}
              </option>
            ))}
        </Select>
        <Select
          label={t('wizard.difficulty_label')}
          value={data.difficulty_level_id}
          onChange={(e) => update('difficulty_level_id', e.target.value)}
        >
          <option value="">-- {t('wizard.difficulty_label')} --</option>
          {(meta.difficulty_levels ?? []).map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </Select>
      </div>

      <TagInput value={data.tag_ids} onChange={(ids) => update('tag_ids', ids)} />
    </>
  );
}

function StepContent({ data, update }: StepProps) {
  const t = useT();
  return (
    <>
      <SectionHeader icon={<Edit3 className="w-4 h-4" />} title={t('wizard.content_section')} subtitle={t('wizard.content_subtitle')} />
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-2">
        <SymbolPicker />
      </div>
      <Textarea
        label={t('wizard.question_label')}
        placeholder={t('wizard.question_ph')}
        value={data.question_text}
        onChange={(e) => update('question_text', e.target.value)}
        rows={6}
        hint={`${data.question_text.trim().length} ${t('wizard.char_count')} — ${t('wizard.symbol_hint')}`}
      />

      <ImageUpload value={data.image_path} onChange={(p) => update('image_path', p)} />

      <Input
        label={t('wizard.points_label')}
        type="number"
        min={1}
        max={100}
        value={data.points}
        onChange={(e) => update('points', parseInt(e.target.value, 10) || 1)}
      />
    </>
  );
}

function StepAnswers({ data, update }: StepProps) {
  const t = useT();

  if (!data.question_type) {
    return <div className="text-sm text-slate-500 py-8 text-center">{t('wizard.no_type')}</div>;
  }

  const symbolBar = (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-2 mb-3">
      <SymbolPicker />
    </div>
  );

  if (data.question_type === 'true_false') {
    return (
      <>
        <SectionHeader icon={<HelpCircle className="w-4 h-4" />} title={t('wizard.true_section')} subtitle={t('wizard.true_subtitle')} />
        {symbolBar}
        <div className="grid grid-cols-2 gap-3">
          {[true, false].map((isTrueAnswer) => {
            const active = data.options[0]?.is_correct === isTrueAnswer;
            return (
              <button
                key={String(isTrueAnswer)}
                type="button"
                onClick={() => {
                  update('options', [
                    { text: 'ถูก', is_correct: isTrueAnswer, explanation: data.explanation },
                    { text: 'ผิด', is_correct: !isTrueAnswer, explanation: data.explanation },
                  ]);
                }}
                className={cn(
                  'p-6 border-2 rounded-xl text-base font-semibold transition-all',
                  active
                    ? isTrueAnswer
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-2 ring-emerald-200'
                      : 'border-red-500 bg-red-50 text-red-700 ring-2 ring-red-200'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                )}
              >
                {isTrueAnswer ? t('wizard.true_label') : t('wizard.false_label')}
              </button>
            );
          })}
        </div>
        <Textarea
          label={t('wizard.explanation_label')}
          placeholder={t('wizard.explanation_ph')}
          value={data.explanation}
          onChange={(e) => update('explanation', e.target.value)}
          rows={3}
        />
      </>
    );
  }

  if (data.question_type === 'matching') {
    return (
      <>
        <SectionHeader icon={<Link2 className="w-4 h-4" />} title={t('wizard.matching_section')} subtitle={t('wizard.matching_subtitle')} />
        {symbolBar}
        <div className="space-y-2">
          {data.pairs.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-slate-400 w-6 text-right">{i + 1}.</span>
              <Input
                placeholder={`${t('wizard.left_ph')} ${i + 1}`}
                value={p.left}
                onChange={(e) => {
                  const next = [...data.pairs];
                  next[i] = { ...p, left: e.target.value };
                  update('pairs', next);
                }}
              />
              <span className="text-slate-400">→</span>
              <Input
                placeholder={t('wizard.right_ph')}
                value={p.right}
                onChange={(e) => {
                  const next = [...data.pairs];
                  next[i] = { ...p, right: e.target.value };
                  update('pairs', next);
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('btn.delete')}
                disabled={data.pairs.length <= 2}
                onClick={() => update('pairs', data.pairs.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => update('pairs', [...data.pairs, { left: '', right: '' }])}
        >
          {t('wizard.add_pair')}
        </Button>
        <Textarea
          label={t('wizard.explanation_label')}
          value={data.explanation}
          onChange={(e) => update('explanation', e.target.value)}
          rows={3}
        />
      </>
    );
  }

  if (data.question_type === 'essay') {
    return (
      <>
        <SectionHeader icon={<FileText className="w-4 h-4" />} title={t('wizard.essay_section')} subtitle={t('wizard.essay_subtitle')} />
        {symbolBar}
        <Textarea
          label={t('wizard.essay_answer_label')}
          placeholder={t('wizard.essay_answer_ph')}
          value={data.model_answer}
          onChange={(e) => update('model_answer', e.target.value)}
          rows={6}
        />
        <Textarea
          label={t('wizard.essay_criteria_label')}
          placeholder={t('wizard.essay_criteria_ph')}
          value={data.scoring_criteria}
          onChange={(e) => update('scoring_criteria', e.target.value)}
          rows={4}
        />
        <Input
          label={t('wizard.points_label')}
          type="number"
          min={1}
          max={100}
          value={data.max_score}
          onChange={(e) => update('max_score', parseInt(e.target.value, 10) || 10)}
        />
      </>
    );
  }

  // single_choice / multiple_answer
  const choiceSubtitle = data.question_type === 'single_choice'
    ? t('wizard.choice_single_subtitle')
    : t('wizard.choice_multi_subtitle');

  return (
    <>
      <SectionHeader
        icon={<ListChecks className="w-4 h-4" />}
        title={t('wizard.answer_section')}
        subtitle={choiceSubtitle}
      />
      {symbolBar}
      <div className="space-y-3">
        {data.options.map((o, i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="pt-2.5">
              <Checkbox
                checked={o.is_correct}
                onChange={() => {
                  let next = [...data.options];
                  if (data.question_type === 'single_choice') {
                    next = next.map((x, idx) => ({ ...x, is_correct: idx === i }));
                  } else {
                    next[i] = { ...o, is_correct: !o.is_correct };
                  }
                  update('options', next);
                }}
              />
            </div>
            <span className="mt-2.5 inline-flex w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold items-center justify-center flex-shrink-0">
              {String.fromCharCode(65 + i)}
            </span>
            <div className="flex-1 space-y-2">
              <Input
                placeholder={`${t('wizard.choice_ph')} ${String.fromCharCode(65 + i)}`}
                value={o.text}
                onChange={(e) => {
                  const next = [...data.options];
                  next[i] = { ...o, text: e.target.value };
                  update('options', next);
                }}
              />
              {o.is_correct && (
                <Input
                  placeholder={t('wizard.choice_correct_expl')}
                  value={o.explanation}
                  onChange={(e) => {
                    const next = [...data.options];
                    next[i] = { ...o, explanation: e.target.value };
                    update('options', next);
                  }}
                />
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('btn.delete')}
              onClick={() => update('options', data.options.filter((_, idx) => idx !== i))}
              disabled={data.options.length <= 2}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<Plus className="w-4 h-4" />}
        disabled={data.options.length >= 8}
        onClick={() => update('options', [...data.options, { text: '', is_correct: false, explanation: '' }])}
      >
        {t('wizard.add_choice')}
      </Button>
    </>
  );
}

function StepReview({
  data, meta, confirmed, setConfirmed,
}: {
  data: WizardData;
  meta: MetaPayload;
  confirmed: boolean;
  setConfirmed: (v: boolean) => void;
}) {
  const t = useT();
  const subj = (meta.subject_groups ?? []).find((s) => String(s.id) === data.subject_group_id);
  const grade = (meta.grade_levels ?? []).find((g) => String(g.id) === data.grade_level_id);
  const cog = (meta.cognitive_levels ?? []).find((c) => String(c.id) === data.cognitive_level_id);
  const diff = (meta.difficulty_levels ?? []).find((d) => String(d.id) === data.difficulty_level_id);
  const typeEntry = TYPES_BASE.find((tp) => tp.value === data.question_type);
  const typeLabel = typeEntry
    ? t(typeEntry.labelKey)
    : '—';

  const safeImg = data.image_path && /^[\w\-./%?&=:;,@+#~]*$/.test(data.image_path) ? data.image_path : '';

  return (
    <>
      <SectionHeader icon={<FileCheck className="w-4 h-4" />} title={t('wizard.review_section')} subtitle={t('wizard.review_subtitle')} />

      <div className="rounded-xl bg-slate-50 border border-slate-100 p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {subj && <Badge tone="brand">{subj.name}</Badge>}
          {grade && <Badge tone="info">{grade.name}</Badge>}
          <Badge tone="neutral">{typeLabel}</Badge>
          <StatusBadge status="draft" />
          {cog && <Badge tone="warning">{cog.name}</Badge>}
          {diff && <Badge tone="neutral">{diff.name}</Badge>}
        </div>

        {data.topic && (
          <div className="text-xs text-slate-500">
            {t('wizard.topic_prefix')} <span className="text-slate-700 font-medium">{data.topic}</span>
          </div>
        )}

        <div className="text-base text-slate-900 whitespace-pre-wrap leading-relaxed">
          {data.question_text || <span className="text-slate-400">{t('detail.no_question')}</span>}
        </div>

        {safeImg && (
          <img
            src={`/${safeImg.replace(/^\.?\//, '')}`}
            alt={t('detail.image_alt')}
            className="max-w-full max-h-64 rounded-lg border border-slate-200"
          />
        )}

        {(data.question_type === 'single_choice' || data.question_type === 'multiple_answer') && (
          <ul className="space-y-1.5">
            {data.options.filter((o) => o.text.trim()).map((o, i) => (
              <li
                key={i}
                className={cn('flex items-start gap-3 px-3 py-2 rounded-lg border', o.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200')}
              >
                <span className={cn('w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center flex-shrink-0', o.is_correct ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600')}>
                  {String.fromCharCode(65 + i)}
                </span>
                <div className="flex-1 text-sm text-slate-800">
                  {o.text}
                  {o.is_correct && <Check className="inline w-4 h-4 text-emerald-600 ml-1.5" />}
                  {o.explanation && (
                    <div className="text-xs text-slate-500 mt-1">{t('detail.explanation')}: {o.explanation}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {data.question_type === 'true_false' && (
          <div className="text-sm">
            {t('detail.answer_label')}: <Badge tone="success">{data.options[0]?.is_correct ? t('wizard.true_label') : t('wizard.false_label')}</Badge>
            {data.explanation && (
              <p className="mt-2 text-xs text-slate-600">{t('detail.explanation')}: {data.explanation}</p>
            )}
          </div>
        )}
        {data.question_type === 'matching' && (
          <ul className="space-y-1.5">
            {data.pairs.filter((p) => p.left && p.right).map((p, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <span className="px-3 py-1 bg-white border border-slate-200 rounded">{p.left}</span>
                <span className="text-slate-400">→</span>
                <span className="px-3 py-1 bg-emerald-50 border border-emerald-200 rounded">{p.right}</span>
              </li>
            ))}
          </ul>
        )}
        {data.question_type === 'essay' && (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">{t('wizard.essay_answer_label')}:</div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-slate-800 whitespace-pre-wrap">
              {data.model_answer || '—'}
            </div>
            {data.scoring_criteria && (
              <>
                <div className="text-xs text-slate-500 mt-2">{t('wizard.essay_criteria_label')}:</div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-slate-800 whitespace-pre-wrap">
                  {data.scoring_criteria}
                </div>
              </>
            )}
            <div className="text-xs text-slate-500">
              {t('wizard.points_label')}: <span className="font-semibold text-slate-700">{data.max_score}</span>
            </div>
          </div>
        )}
      </div>

      <label className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 cursor-pointer">
        <Checkbox checked={confirmed} onChange={() => setConfirmed(!confirmed)} />
        <span className="text-sm text-amber-800">
          {t('wizard.confirm_label')}
        </span>
      </label>
    </>
  );
}

function SectionHeader({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle?: string }) {
  return (
    <div className="flex items-start gap-3 pb-2 border-b border-slate-100">
      <div className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center flex-shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
}

// ─── Image upload ───────────────────────────────────
function ImageUpload({ value, onChange }: { value: string; onChange: (path: string) => void }) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const displayUrl = previewUrl || (value ? `/${value.replace(/^\.?\//, '')}` : '');

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      toast.error(t('wizard.image_err_type'), t('wizard.image_err_type_hint'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('wizard.image_err_size'), t('wizard.image_err_size_hint'));
      return;
    }
    setPreviewUrl(URL.createObjectURL(file));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const token = localStorage.getItem('auth_token');
      const res = await fetch('/api/questions/upload-image.php', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || t('wizard.image_fail'));
      // Backend returns { url, filename, size, type, original_name }.
      // Earlier code looked for `image_path` which never existed and caused
      // every successful upload to fail with "no path returned".
      const path = json.data?.url || json.data?.image_path || json.data?.path || json.url;
      if (!path) throw new Error(t('wizard.server_no_path'));
      onChange(path);
      toast.success(t('wizard.image_success'));
    } catch (e) {
      toast.error(t('wizard.image_fail'), (e as Error).message);
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  }

  function clear() {
    onChange('');
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
        <ImageIcon className="inline w-4 h-4 mr-1" /> {t('wizard.image_label')}
      </label>
      {displayUrl ? (
        <div className="relative inline-block group">
          <img
            src={displayUrl}
            alt={t('detail.image_alt')}
            className="max-w-full max-h-72 rounded-lg border border-slate-200"
          />
          <button
            type="button"
            onClick={clear}
            aria-label={t('btn.delete')}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center shadow-md hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-lg">
              <span className="h-6 w-6 rounded-full border-2 border-brand-600 border-t-transparent animate-spin" />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full border-2 border-dashed border-slate-300 rounded-lg px-4 py-8 flex flex-col items-center gap-2 text-slate-500 hover:border-brand-400 hover:bg-brand-50/30 transition-colors"
        >
          {uploading ? (
            <span className="h-6 w-6 rounded-full border-2 border-brand-600 border-t-transparent animate-spin" />
          ) : (
            <Upload className="w-7 h-7" />
          )}
          <span className="text-sm font-medium">
            {uploading ? t('wizard.image_uploading') : t('wizard.image_click')}
          </span>
          <span className="text-xs text-slate-400">{t('wizard.image_size')}</span>
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
    </div>
  );
}
